import type { DrawingPayload, DrawingType } from '@stockstop/shared';
import { channelRenderer } from './channel.js';
import { horizontalLineRenderer } from './horizontal-line.js';
import { stopLossLineRenderer } from './stop-loss-line.js';
import { trendLineRenderer } from './trend-line.js';
import type { DrawingRenderer } from './types.js';

/**
 * Renderer registry.
 *
 * Typed as `{ [T in DrawingType]: DrawingRenderer<T> }`, so adding a drawing type
 * to the shared union without adding a renderer here is a compile error. It is the
 * same mechanism the stop-loss calculators use on the server.
 */
const RENDERERS: { [T in DrawingType]: DrawingRenderer<T> } = {
  stop_loss: stopLossLineRenderer,
  horizontal_line: horizontalLineRenderer,
  trend_line: trendLineRenderer,
  channel: channelRenderer,
};

/**
 * Looks up the renderer for a payload.
 *
 * The cast re-attaches the payload to its own renderer: TypeScript cannot prove
 * through an indexed access that `RENDERERS[payload.type]` matches `payload`, even
 * though the registry's type guarantees it. Confining the cast here keeps every
 * renderer strictly typed at its definition and at its call sites.
 */
export const rendererFor = (payload: DrawingPayload): DrawingRenderer<DrawingType> =>
  RENDERERS[payload.type] as DrawingRenderer<DrawingType>;

export { RENDERERS };
